import {
  ui,
  Map,
  TileLayer,
  VectorLayer,
  Extent,
  LineString,
  Geometry,
  animation,
  Marker,
  Coordinate,
  Layer,
} from "maptalks"
import "@maptalks/transcoders.draco"
import TWEEN from "@tweenjs/tween.js"
import _ from "lodash"
import {
  feature as turfFeature,
  featureCollection,
  lineString,
  point,
} from "@turf/helpers"

import {
  LineString as LineStringType,
  Feature,
  GeoJsonProperties,
} from "geojson"
import turfDistance from "@turf/distance"
import turfCenter from "@turf/center"
import bbox from "@turf/bbox"
import scale from "@turf/transform-scale"
import bboxPolygon from "@turf/bbox-polygon"

import { PerspectiveCamera, Clock } from "three"
import { ThreeLayer } from "maptalks.three"
import { GLTFLayer, GLTFMarker } from "@maptalks/gltf-layer"
import { GroupGLLayer } from "@maptalks/gl"
import Color from "color"

import {
  LAYERS,
  LAYER_OPTIONS,
  LAYER_FEATURE_TYPE_OBJ,
  HIGHLIGHT_LAYER_NAME,
  USER_LOCATION_LAYER_NAME,
  ORIGIN_MARKER_ID,
  DESTINATION_MARKER_ID,
  DEFAULT_HIGHLIGHT_OPTIONS,
  DEFAULT_SET_HIGHLIGHT_2DELEMENT_IDS_OPTIONS,
  USER_LOCATION_ELEMENT_ID,
  LAST_USER_LOCATION_ELEMENT_ID_PREFIX,
  ALWAYS_VISIBLE_FEATURE_TYPES,
  PRESERVE_OPACITY_FEATURE_TYPES,
  LOCALE_SYMBOL_KEY,
  DEFAULT_LOCALE,
  VENUE_EVENTS,
  POI_MARKER_LAYER_NAME,
  SUFFIX_MARKER,
  DEFAULT_EXTRUDE_CONFIGS,
  ORIGIN_MARKER_SPRITE_SHEET_FRAME_COUNT,
  ORIGIN_MARKER_SPRITE_ANIMATION_FPS,
  BASE_LAYER_NAME,
  DEFAULT_BASEMAP,
} from "./constants"
import { ISetHighlightElementIdsOptions } from "./interface"
import { getSectionSymbol, getSectionFlipZoom } from "theme/map"
import { compileZoomStyle } from "./utils/zoomStyle"

import {
  styledFeatureGenerator,
  getExtrudeConfigByFeature,
  buildCompositeId,
  getMarkerId,
  createStyledUIMarkerElement,
  applyOriginMarkerSpriteFrame,
} from "./utils/createElements"

import { getBearingBetweenPoints, getOctantFromBearing } from "./utils/math"

import {
  NavigationPath,
  Billboard,
  SpriteMarker,
  GroundLabel,
} from "./object3d"
import {
  createHighlightExtrudeObjectController,
  createHighlightBillboardController,
  createHighlightSectionVisibilityController,
} from "./utils/createHighlightElement"
import { IMapConfig, IBasemapConfig } from "services/venue"
import config from "config"
import { IFlyToMapOptions } from "hooks/map"
import { angleBetweenLineStrings } from "./utils/angleBetweenLineStrings"
import { ImdfFeature } from "venue-react"
import booleanPointInPolygon from "@turf/boolean-point-in-polygon"

const defaultOptions = {
  center: [100.5017051, 13.7572619],
  zoom: 18.5,
  bearing: 0,
  pixelRatio: 1,
  locale: DEFAULT_LOCALE,
}

const CLICK_TOLERANCE = 20

// How long to wait for the browser to restore a lost WebGL context on its own
// before giving up and reloading. Under sustained GPU memory pressure the
// browser commonly never restores it, and nothing else throws when that
// happens (three.js just stops drawing), so the map goes blank forever with
// no other recovery path.
const WEBGL_CONTEXT_RESTORE_GRACE_MS = 5000

export class IndoorMap {
  //TODO: refac functions; let them do only 1 thing in a function

  /** Note: "#" means private variables */
  #defaultCenter = defaultOptions.center
  #defaultZoom = defaultOptions.zoom
  #defaultBearing = defaultOptions.bearing
  #defaultPitch
  #styler = null
  #featuresInitted = false
  #elementsLoaded = false
  #elements: Record<string, any> = {}
  #features = []
  #venues = []
  #venueInView: string | null = null
  #detectingVenueInView = false
  #ordinals = []
  #mapTheme = {}
  #billboards = []
  #billboardObjects = []
  // NOTE: transform `billboardObjects` by id for faster lookup, apply the same approach to `object3ds` in the future if needed.
  #billboardObjectsById: Record<string, Billboard> = {}
  #spriteMarkerObjects = []
  #mapDecorations = []
  #groundLabels = []
  #groundObjects = []
  #navigationGeometries: Record<string, NavigationPath> = {}
  #venueMarkers: GLTFMarker[] = []
  #glbObjects = []
  #gltfMarkers = []
  #objects = []
  #object3ds = []
  // Extruded 3D bodies whose material is driven by zoom via a compiled
  // zoom->style fn (extruded sections whose theme tier defines
  // polygonOpacity.stops enrol here) — see handleMapZoomEnd / render() /
  // #applyStyleOnZoom.
  #zoomStyledObjects = []
  #model3ds = []
  #isClicked = false
  #highlightElementIds = []
  #highlightObjectIds = []
  #highlightObjectControllers = []
  #highlight2DMarkers = []
  #persistentHighlightElementIds: string[] = []
  #persistentHighlightOptions: ISetHighlightElementIdsOptions = {}
  #userLocationElement = null
  #mapConfig: IMapConfig = {}
  #basemap: IBasemapConfig = DEFAULT_BASEMAP
  #locale: string = defaultOptions.locale
  #lastUserLocationGeometries = []
  #lastUserLocationElements = []
  #currentOrdinal: number | null = null

  #zoomLevel = defaultOptions.zoom

  #isProgrammaticFlying = false

  #isLayersFadingOnZoom = false

  // Identifies markers/labels that hide during a drag-rotate or drag-pan
  // gesture: occupant markers, amenity markers, plus the emergency-exit
  // opening marker.
  #isHiddenDuringGestureFeature = (properties: any = {}): boolean => {
    const { feature_type, category } = properties
    if (feature_type === "occupant" || feature_type === "amenity") return true
    if (feature_type === "opening" && category === "emergencyexit") return true
    return false
  }

  #touchStartTarget = null
  #touchStartPoint = null

  #onClickElement = (e) => {}
  #animationsToRun = []

  #webglContextLostAt: number | null = null

  map: Map | null = null
  showVenueObject = false
  threeLayer: ThreeLayer | null = null
  gltfLayer: GLTFLayer | null = null
  clock = new Clock()

  onMapReady = () => {}
  onMapLoading = () => {}
  onModelReady = () => {}

  constructor(elementId, options) {
    const {
      center,
      defaultZoom,
      onMapReady,
      onModelReady,
      onMapLoading,
      pixelRatio,
      locale,
    } = _.merge({}, defaultOptions, options)
    // To be moved to config
    this.#defaultZoom = defaultZoom
    this.#zoomLevel = defaultZoom

    this.map = new Map(elementId, {
      attribution: false,
      center,
      zoom: defaultZoom,
      clickTimeThreshold: 600,
      baseLayer: this.#createBaseLayer(DEFAULT_BASEMAP),
      layers: [],
    })
    this.locale = locale
    this.map.setDevicePixelRatio(pixelRatio)
    this.onMapReady = onMapReady
    this.onMapLoading = onMapLoading
    this.onModelReady = onModelReady

    // Bind events
    this.map.on("zooming", this.handleMapZooming)
    this.map.on("zoomend", this.handleMapZoomEnd)
    this.map.on("click", this.handleMapClick)
    this.map.on("dragrotatestart", this.handleClaimSpriteFacing)
    this.map.on("dragrotatestart", this.handleMapDragRotateStart)
    this.map.on("dragrotateend", this.handleMapDragRotateEnd)
    this.map.on("movestart", this.handleMapMoveStart)
    this.map.on("moveend", this.handleMapMoveEnd)
    this.map.on("animateinterrupted", () => {
      // When an animation is interrupted, cancel the previous player instance
      // to prevent errors (e.g., flyTo) caused by an unexpected undefined player.
      //@ts-ignore
      this.map._animPlayer?.cancel()
    })
  }

  // Builds the basemap tile layer from config, merged over DEFAULT_BASEMAP.
  // Returns undefined when disabled, so the map renders with no basemap at all.
  #createBaseLayer(basemapConfig: IBasemapConfig): TileLayer | undefined {
    const {
      enabled,
      url_template,
      subdomains,
      opacity,
      css_filter,
      max_available_zoom,
    } = _.merge({}, DEFAULT_BASEMAP, basemapConfig) as Required<IBasemapConfig>

    if (enabled === false) return undefined

    return new TileLayer(BASE_LAYER_NAME, {
      urlTemplate: url_template,
      ...(subdomains?.length ? { subdomains } : {}),
      opacity,
      maxAvailableZoom: max_available_zoom,
      // An empty cssFilter would set ctx.filter = "" instead of leaving it
      // untouched, so only pass it through when there is one.
      ...(css_filter ? { cssFilter: css_filter } : {}),
      hitDetect: false,
      decodeImageInWorker: true,
      errorUrl: "/assets/img/tile-placeholder.png",
    })
  }

  /**
   * Events
   */

  /**
   * NOTE: move zoom-based marker visibility logic to `handleMapZoomEnd` instead of `#createElements`
   * because uniform stops (e.g. polygonOpacity, lineOpacity with { stops: [[zoom, value], ...] })
   * are evaluated only once when `_prepareSymbol` runs (on symbol set), not on zoom change.
   * So `getUniforms()` returns a static value. As a result:
   *
   * - zoom-in from low zoom: works (initial value was computed at low zoom; marker may get dirty / re-evaluated during zoom-in)
   * - zoom-out from high zoom: does not work (stops were evaluated at high zoom once, value stays fixed; no re-evaluation on zoom change).
   *
   * `marker.setUniform()` marking the marker as dirty when zoom changes
   */
  handleMapZoomEnd = (): void => {
    // zoom = 19, opacity = 0
    // zoom = 18.5, opacity = 1
    const objectOpacity = _.clamp(38 - 2 * this.#zoomLevel, 0, 1)

    const venueMarkers = this.#venueMarkers
    venueMarkers?.forEach((marker) => {
      marker.setUniform("polygonOpacity", objectOpacity)
      marker.setUniform("lineOpacity", objectOpacity)
    })

    this.#applyStyleOnZoom()
  }

  // Drives enrolled extruded 3D bodies' material from the current zoom via
  // each mesh's compiled zoom->style fn.
  #applyStyleOnZoom = (): void => {
    this.#zoomStyledObjects.forEach(({ apply }) => {
      apply(this.#zoomLevel)
    })
  }

  handleMapZooming = (e: any) => {
    this.#zoomLevel = e.to
  }

  handleMapClick = (e: any) => {
    const { coordinate } = e
    const { x, y } = coordinate

    console.log(
      `[Coordinates]: x: ${_.round(x, 8)} y: ${_.round(
        y,
        8
      )}, [Bearing]: ${this.map.getBearing()}, [Pitch]: ${this.map.getPitch()}`
    )
  }

  // Occupant/amenity markers and labels are hidden for the duration of a
  // drag-rotate or map-move (pan) gesture and restored once it ends, since
  // their bearing-flip transitions (see GroundLabel `bearing` setter /
  // render()) flicker while the camera is actively moving.
  handleMapDragRotateStart = (): void => {
    this.#setGestureHiddenElementsVisibility(false)
  }

  handleMapDragRotateEnd = (): void => {
    this.#setGestureHiddenElementsVisibility(true)
  }

  handleMapMoveStart = (): void => {
    this.#setGestureHiddenElementsVisibility(false)
  }

  handleMapMoveEnd = (): void => {
    this.#setGestureHiddenElementsVisibility(true)
  }

  #setGestureHiddenElementsVisibility = (visible: boolean): void => {
    const toggle = (obj) => (visible ? obj?.show?.() : obj?.hide?.())

    // All ground labels (occupant flat labels, unit/decoration labels, etc.)
    this.#groundObjects.forEach(toggle)

    this.#object3ds.forEach((obj) => {
      if (this.#isHiddenDuringGestureFeature(obj?.properties)) toggle(obj)
    })

    Object.values(this.#elements).forEach((element: any) => {
      const properties = {
        feature_type: element?.featureType,
        category: element?.properties?.category,
      }
      if (this.#isHiddenDuringGestureFeature(properties))
        toggle(element.geometry)
    })
  }

  handleMapTouchEnd = (e: any) => {
    const { containerPoint } = e
    const { x, y } = containerPoint
    const { x: touchStartX, y: touchStartY } = this.#touchStartPoint

    const dis = Math.hypot(x - touchStartX, y - touchStartY)
    this.map.off("touchend", this.handleMapTouchEnd)

    if (dis > 0 && dis < CLICK_TOLERANCE) {
      console.log("Forgiving click", this.#touchStartTarget.properties.id)
      this.handleClickElement({ target: this.#touchStartTarget })
    }

    this.#touchStartTarget = null
    this.#touchStartPoint = null
  }

  /**
   * Getters & Setters
   */
  get defaultBearing() {
    return this.#defaultBearing
  }

  get defaultPitch() {
    return this.#defaultPitch
  }

  get defaultCenter() {
    return this.#defaultCenter
  }

  get defaultZoom() {
    return this.#defaultZoom
  }

  get elementsLoaded() {
    return this.#elementsLoaded
  }

  get pixelRatio() {
    return this.map.getDevicePixelRatio()
  }

  get locale() {
    return this.#locale
  }

  /**
   * Get current map view
   * @returns {Object|null} Current map view with center, zoom, pitch, bearing, timestamp
   */
  getCurrentMapView = () => {
    return {
      center: this.map.getCenter(),
      zoom: this.map.getZoom(),
      pitch: this.map.getPitch(),
      bearing: this.map.getBearing(),
      timestamp: Date.now(),
    }
  }

  set mapTheme(value) {
    this.#mapTheme = value
    this.#styler = styledFeatureGenerator(this.#mapTheme)
  }

  get venues() {
    return this.#venues || []
  }

  #findAndSetVenueInView = () => {
    const venueInViewWithDistance = this.findVenueInView()
    if (
      venueInViewWithDistance &&
      (!this.#venueInView ||
        venueInViewWithDistance.venueId !== this.#venueInView)
    ) {
      this.#venueInView = venueInViewWithDistance.venueId
      this.map.fire(VENUE_EVENTS.VENUE_MOVEINTOVIEW, venueInViewWithDistance)
    }
  }

  set detectVenueInView(value) {
    value = Boolean(value)

    // Prevent re-attaching the same event listener when the state hasn't changed
    if (value === this.#detectingVenueInView) return

    this.#detectingVenueInView = value

    if (value) {
      // Set initial venueInView
      this.#findAndSetVenueInView()
      this.map.on("moveend", this.#findAndSetVenueInView)
    } else {
      this.map.off("moveend", this.#findAndSetVenueInView)
    }
  }

  get ordinals() {
    return this.#ordinals || []
  }

  set ordinals(value) {
    if (!Array.isArray(value)) throw new Error("ordinals must be Array")
    this.#ordinals = value
  }

  set billboards(value) {
    this.#billboards = value
  }

  set defaultCenter(value) {
    this.#defaultCenter = value
  }
  set defaultBearing(value) {
    this.#defaultBearing = value
  }
  set defaultPitch(value) {
    this.#defaultPitch = value
  }
  set defaultZoom(value) {
    this.#defaultZoom = value
  }
  set mapConfig(value: IMapConfig) {
    this.#mapConfig = value
  }

  set mapDecorations(value) {
    this.#mapDecorations = value
  }

  set basemap(value: IBasemapConfig) {
    this.#basemap = value
    this.map.removeBaseLayer()
    const baseLayer = this.#createBaseLayer(value)
    if (baseLayer) this.map.setBaseLayer(baseLayer)
  }

  set maxZoom(value: number) {
    const spatialReference = {
      projection: "EPSG:3857",
      resolutions: (function () {
        const resolutions = []
        const d = 2 * 6378137 * Math.PI
        for (let i = 0; i <= value; i++) {
          resolutions[i] = d / (256 * Math.pow(2, i))
        }
        return resolutions
      })(),
    }

    this.map.setSpatialReference(spatialReference)
    this.map.setMaxZoom(value)
  }

  set minZoom(value: number) {
    this.map.setMinZoom(value)
  }

  set groundLabels(value) {
    this.#groundLabels = value
  }

  set pixelRatio(value: number) {
    this.map.setDevicePixelRatio(value)
  }

  set onClickElement(func) {
    this.#onClickElement = func
  }

  set locale(value: string) {
    this.#locale = value || defaultOptions.locale

    // Update elements affected by locale changes.
    this.updateUserLocationSymbolByLocale(this.#locale)
  }

  set features(value) {
    /**
     * Setup with features
     * -------------------
     * 1. Create ordinals
     * 2. Init Layers (based on ordinals)
     * 3. Create elements
     */
    if (value.length === 0 || !this.#mapTheme) {
      return
    }

    this.#features = value

    this.#venues = _(this.#features)
      .filter((f) => f.feature_type === "venue")
      .value()

    this.ordinals = _(this.#features)
      .filter((f) => f.feature_type === "level")
      .map((f) => f.properties.ordinal)
      .sort()
      .uniq()
      .value()

    if (typeof this.onMapLoading === "function") this.onMapLoading()

    if (this.#featuresInitted) {
      this.#clearElements()
      this.#createElements()
      return
    }
    this.#initMapElements()
  }

  set model3ds(value) {
    this.#model3ds = value
  }

  /**
   * Private internal methods
   */

  #clearElements() {
    LAYERS.forEach((layerKey) => {
      this.#clearAllElementOnLayerByName(layerKey)
    })
    const scene = this.threeLayer.getScene()
    if (scene) {
      scene.children = scene.children.filter(
        (children) => children instanceof PerspectiveCamera
      )
    }
    this.#zoomStyledObjects = []
  }

  #createLayers() {
    const map = this.map
    // Clear Existing Layers
    if (!this.ordinals || this.ordinals.length === 0) return
    // Create Layers
    LAYERS.forEach((layerKey) => {
      const layerID = layerKey
      const option = LAYER_OPTIONS[layerKey]
      const layer = new VectorLayer(layerID, option)
      map.addLayer(layer)
    })

    // ThreeJS Layer
    const threeLayer = new ThreeLayer("t_building", {
      forceRenderOnMoving: true,
      forceRenderOnRotating: true,
    })
    this.threeLayer = threeLayer

    // GLTF Layer
    const gltfLayer = new GLTFLayer("gltf")
    this.gltfLayer = gltfLayer

    const groupLayer = new GroupGLLayer("group", [], {}).addTo(this.map)

    groupLayer.addLayer(threeLayer as unknown as Layer)
    groupLayer.addLayer(gltfLayer)

    //@ts-ignore
    threeLayer.addTo(this.map)
    //@ts-ignore
    gltfLayer.addTo(this.map)
  }
  #initMapElements() {
    this.#createLayers()
    const threeLayer = this.threeLayer
    if (threeLayer) {
      this.threeLayer.prepareToDraw = async (gl, scene, camera) => {
        await this.#createElements()
      }
    }
    this.#setupWebglContextLossRecovery()
  }

  // webglcontextlost/restored don't bubble, but a capture-phase listener on
  // an ancestor still sees them regardless of which canvas they fire on.
  #setupWebglContextLossRecovery() {
    const container = this.map.getContainer()
    container.addEventListener(
      "webglcontextlost",
      this.#handleWebglContextLost,
      true
    )
    container.addEventListener(
      "webglcontextrestored",
      this.#handleWebglContextRestored,
      true
    )
  }

  #handleWebglContextLost = (event: Event) => {
    // Without preventDefault() the browser never attempts to restore the context.
    event.preventDefault?.()
    console.error("[IndoorMap] WebGL context lost")
    this.#webglContextLostAt = Date.now()
    window.setTimeout(() => {
      if (!this.#webglContextLostAt) return
      console.error(
        "[IndoorMap] WebGL context did not restore in time — reloading"
      )
      if ((window as any).electron) {
        ;(window as any).electron.sendMessage("relaunch")
      } else {
        window.location.reload()
      }
    }, WEBGL_CONTEXT_RESTORE_GRACE_MS)
  }

  #handleWebglContextRestored = () => {
    console.info("[IndoorMap] WebGL context restored")
    this.#webglContextLostAt = null
  }

  handleClickElement = (e: any) => {
    // Click-flip: once zoomed past a faded section's flipZoom, its extruded
    // body is fully faded (opacity 0) and must stop intercepting clicks so
    // the ray falls through to the individual unit meshes below.
    const targetProperties = e?.target?.properties
    if (
      targetProperties?.flipZoom != null &&
      this.#zoomLevel >= targetProperties.flipZoom
    ) {
      return
    }

    if (this.#isClicked) return
    this.#isClicked = true
    const onClickElement = this.#onClickElement
    if (!_.isFunction(onClickElement)) return
    this.#onClickElement(e)
    this.#isClicked = false
  }

  setCenter(center: Coordinate) {
    this.map.setCenter(center)
  }

  async #createElements() {
    const {
      // 2D
      createVenue,
      createLevel,
      createUnit,
      createOpening,
      createFixture,
      createKiosk,
      createOccupant,
      createDecoration,
      createSection,
      createSectionMarker,
      // 3D
      create3DFootprint,
      create3DGroundLabel,
      create3DBillboard,
      create3DUnit,
      create3DKiosk,
      createExtrudedUnit,
      createExtrudedLevel,
      create3DFixture,
      createExtrudedKiosk,
      createExtrudedSection,
      renderSectionMarker,
      create3DOccupantAmenityMarker,
      create3DOpeningMarker,
      createOccupantGroundLabel,
      createModel3d,
      create3dEscalator,
      // Renderer
      renderAmenityMarker,
      // Light
      createAmbientLight,
      createDirectionalLight,
    } = this.#styler

    let elements = {}
    let object3ds = []

    // Prepare extrude config before creating extruded elements
    const extrudeConfig = _.get(this.#mapConfig, "extrude")

    const mergedExtrudeConfig = _.merge(
      {},
      DEFAULT_EXTRUDE_CONFIGS,
      extrudeConfig
    )

    const scene = this.threeLayer.getScene()

    if (scene) {
      const {
        ambientLight: ambientLightConfig = {},
        directionalLight: directionalLightConfig = {},
      } = _.get(this.#mapConfig, "light", {
        ambientLight: {},
        directionalLight: {},
      })

      const ambientLight = createAmbientLight(ambientLightConfig)
      scene.add(ambientLight)

      const light = createDirectionalLight(directionalLightConfig)
      scene.add(light)
    }
    for (const feature of this.#features) {
      try {
        const { feature_type: featureType, properties, id } = feature
        const layerName = _.get(
          LAYER_FEATURE_TYPE_OBJ,
          featureType,
          featureType
        )
        const layer = this.map.getLayer(layerName)
        let geometry
        const category = _.get(feature, "properties.category")

        const textMarkerType = _.get(
          this.#mapConfig,
          "text_marker_type",
          "ui-marker"
        )

        // Prepare extrude config before creating extruded elements
        const extrudeConfig = _.get(this.#mapConfig, "extrude")
        const mergedExtrudeConfig = _.merge(
          {},
          DEFAULT_EXTRUDE_CONFIGS,
          extrudeConfig
        )
        const featureExtrudeConfig = getExtrudeConfigByFeature(
          mergedExtrudeConfig,
          feature
        )

        switch (featureType) {
          case "venue": {
            geometry = createVenue(feature).addTo(layer)

            //TODO: remove create venue model in the future
            const model3ds = feature.properties.model3d

            model3ds.forEach((model3d) => {
              const marker = new GLTFMarker(model3d.properties.position, {
                /**
                 * set `isAdd: true` as the initial state
                 * and toggle model3d visibility via `showVenueObjects` / `hideVenueObjects`.
                 */
                symbol: {
                  url: model3d.url,
                  scaleX: model3d.properties.scale[0] || 1,
                  scaleY: model3d.properties.scale[1] || 1,
                  scaleZ: model3d.properties.scale[2] || 1,
                  rotationX: model3d.properties.rotation.x,
                  rotationY: model3d.properties.rotation.y,
                  rotationZ: model3d.properties.rotation.z,
                  uniforms: {
                    polygonFill: model3d.properties?.polygonFill || [
                      1, 1, 1, 1,
                    ],
                  },
                },
              })

              marker.properties = { isAdd: true }

              this.#venueMarkers.push(marker)
              marker.addTo(this.gltfLayer)
            })

            break
          }
          case "level": {
            const extrudedLevel = createExtrudedLevel(
              feature,
              this.threeLayer,
              featureExtrudeConfig
            )

            if (extrudedLevel) {
              object3ds.push(...extrudedLevel)
            } else {
              geometry = createLevel(feature).addTo(layer)
            }
            break
          }
          case "unit": {
            const { category } = feature.properties

            const models = await create3DUnit(feature, this.threeLayer)

            models.forEach((model) => {
              model.on("click", this.handleClickElement)
              object3ds.push(model)
              this.#glbObjects.push(model)
            })
            const locatedLevel = feature?.properties?.level
            const levelExtrudeConfig = getExtrudeConfigByFeature(
              mergedExtrudeConfig,
              locatedLevel
            )

            const levelHeight = levelExtrudeConfig?.height ?? 0

            const option = {
              ...featureExtrudeConfig,
              altitude: levelHeight,
            }

            const hasAddedModel3d = models?.length > 0

            if (hasAddedModel3d || !featureExtrudeConfig) {
              geometry = createUnit(feature)
                ?.on("click", this.handleClickElement)
                .addTo(layer)
            } else {
              if (
                category === "escalator" &&
                config("escalator3dModelFeatureFlag")
              )
                break
              const extrudedUnit = createExtrudedUnit(
                feature,
                this.threeLayer,
                option
              )

              if (!extrudedUnit) break

              const [mainMesh] = extrudedUnit

              mainMesh.on("click", this.handleClickElement)

              // Forgiving Click
              mainMesh.on("touchstart", (e) => {
                this.#touchStartTarget = e.target
                this.#touchStartPoint = e.containerPoint
                this.map.on("touchend", this.handleMapTouchEnd)
              })

              object3ds.push(...extrudedUnit)
            }
            break
          }
          case "amenity": {
            const amenityMarker = renderAmenityMarker(
              feature,
              this.threeLayer,
              extrudeConfig
            )

            if (amenityMarker) {
              amenityMarker?.on("click", this.handleClickElement)
              this.#spriteMarkerObjects.push(amenityMarker)
              object3ds.push(amenityMarker)
            }

            if (amenityMarker instanceof Billboard) {
              this.#billboardObjects.push(amenityMarker)
              this.#billboardObjectsById[amenityMarker.properties.id] =
                amenityMarker
            }
            break
          }
          case "opening": {
            switch (category) {
              case "emergencyexit":
                const { geometry } = turfCenter(feature)
                const markerFeature = {
                  ...feature,
                  geometry,
                }
                const marker3d = create3DOpeningMarker(
                  markerFeature,
                  this.threeLayer,
                  extrudeConfig
                )?.on("click", this.handleClickElement)
                object3ds.push(marker3d)
                break
              default:
            }
            geometry = createOpening(feature)
              ?.on("click", this.handleClickElement)
              .addTo(layer)
            break
          }
          case "section": {
            let polygon
            let extrudedBody = []
            const markers2d = []
            const sprites3d = []

            const { level: locatedLevel, unit, kiosk } = feature?.properties

            const levelExtrudeConfig = getExtrudeConfigByFeature(
              mergedExtrudeConfig,
              locatedLevel
            )
            const levelHeight = levelExtrudeConfig?.height ?? 0

            const locatedFeature = unit || kiosk
            const locatedFeatureExtrudeConfig = locatedFeature
              ? getExtrudeConfigByFeature(mergedExtrudeConfig, locatedFeature)
              : null

            const offsetAltitude = locatedFeatureExtrudeConfig?.height ?? 0

            const option = {
              ...featureExtrudeConfig,
              altitude: levelHeight + offsetAltitude,
              sectionAltitude: _.get(featureExtrudeConfig, "height", 0),
            }

            if (!featureExtrudeConfig) {
              polygon = createSection(feature)

              const sectionMarker = createSectionMarker(feature, null, {
                extrudeConfig,
              })
              if (sectionMarker) markers2d.push(sectionMarker)
            } else {
              const extrudeSection = createExtrudedSection(
                feature,
                this.threeLayer,
                option
              )

              if (extrudeSection) {
                extrudedBody = extrudeSection
                const [mainMesh] = extrudeSection

                // Fade-enroll the extruded body iff its tier's theme symbol
                // defines polygonOpacity.stops — fading is opt-in per venue,
                // not gated by render_type (see theme/map/index.js).
                const sectionSymbol = getSectionSymbol(
                  properties.render_priority,
                  this.#mapTheme
                )
                // Compiled once here, not per-frame, since each interpolator
                // returns a new fn per call.
                if (sectionSymbol?.polygonOpacity?.stops) {
                  const flipZoom = getSectionFlipZoom(
                    properties.render_priority,
                    this.#mapTheme
                  )
                  const apply = compileZoomStyle(sectionSymbol, mainMesh, [
                    "polygonOpacity",
                  ])

                  mainMesh.properties = {
                    ...mainMesh.properties,
                    flipZoom,
                  }

                  this.#zoomStyledObjects.push({ mesh: mainMesh, apply })
                  // Paint at the live zoom immediately — #zoomStyledObjects is
                  // otherwise only driven by the cached #zoomLevel (updated on
                  // zooming/zoomend), which could still hold defaultZoom here
                  // and cause a visible snap on the first zoom gesture.
                  apply(this.map.getZoom())
                }

                // Outline mesh(es) (extrudeSection[1..], e.g. topOutline)
                // fade independently on lineOpacity.stops — strictly opt-in,
                // does not fall back to polygonOpacity.
                if (sectionSymbol?.lineOpacity?.stops) {
                  extrudeSection.slice(1).forEach((outlineMesh) => {
                    const applyOutline = compileZoomStyle(
                      sectionSymbol,
                      outlineMesh,
                      ["lineOpacity"]
                    )
                    this.#zoomStyledObjects.push({
                      mesh: outlineMesh,
                      apply: applyOutline,
                    })
                    applyOutline(this.map.getZoom())
                  })
                }

                if (
                  properties.render_type === "Name" ||
                  properties.render_type === "Logo + Name"
                ) {
                  // Persistent 2D text marker, not the 3D sprite path.
                  const sectionMarker2d = createSectionMarker(feature, null, {
                    extrudeConfig,
                  })
                  if (sectionMarker2d) markers2d.push(sectionMarker2d)
                } else {
                  const sectionMarker3d = renderSectionMarker(
                    feature,
                    this.threeLayer,
                    option
                  )
                  if (sectionSymbol?.markerOpacity?.stops) {
                    const [marker] = sectionMarker3d
                    const applyMarker = compileZoomStyle(
                      sectionSymbol,
                      marker,
                      ["markerOpacity"]
                    )
                    this.#zoomStyledObjects.push({
                      mesh: marker,
                      apply: applyMarker,
                    })
                    applyMarker(this.map.getZoom())
                  }
                  if (sectionMarker3d?.length)
                    sprites3d.push(...sectionMarker3d)
                }
              }
            }

            geometry = polygon?.on("click", this.onClickElement).addTo(layer)

            if (extrudedBody.length) {
              const [mainMesh] = extrudedBody

              mainMesh.on("click", this.handleClickElement)

              // Forgiving Click
              mainMesh.on("touchstart", (e) => {
                this.#touchStartTarget = e.target
                this.#touchStartPoint = e.containerPoint
                this.map.on("touchend", this.handleMapTouchEnd)
              })

              object3ds.push(...extrudedBody)
            }

            markers2d.forEach((marker) => {
              marker.on("click", this.handleClickElement)

              if (marker instanceof ui.UIMarker) {
                marker.addTo(this.map)
              } else {
                const sectionMarkerLayer = this.map.getLayer(
                  POI_MARKER_LAYER_NAME
                )
                marker.addTo(sectionMarkerLayer)
              }

              if (id) {
                const markerId = getMarkerId(marker)
                elements[markerId] = {
                  geometry: marker,
                  properties,
                  featureType: "section-marker",
                  feature,
                }
              }
            })

            sprites3d.forEach((marker) => {
              marker.on("click", this.handleClickElement)
              this.#spriteMarkerObjects.push(marker)
              object3ds.push(marker)
            })

            break
          }
          case "occupant": {
            switch (category) {
              // Create only marker if it is amenity occupant
              case "currencyexchange":
              case "donationcenter":
              case "postoffice":
                const markerFeature = {
                  ...feature,
                  geometry: feature.properties?.anchor?.geometry,
                }
                const marker3d = create3DOccupantAmenityMarker(
                  markerFeature,
                  this.threeLayer,
                  extrudeConfig
                )?.on("click", this.handleClickElement)
                object3ds.push(marker3d)
                break
              default: {
                // Main Location
                const { kiosk, unit } = feature.properties

                let mainLocation = kiosk || unit || null

                const relatedLocations = [
                  ...feature.properties.units,
                  ...feature.properties.kiosks,
                ]

                // related locations excluding current ordinal
                const relatedLocationsWithoutSameOrdinalLocations = [
                  ...feature.properties.units,
                  ...feature.properties.kiosks,
                ].filter((f) => f.properties.ordinal !== properties.ordinal)

                const showNameonAllUnits =
                  feature.properties.show_name_on_all_units

                // Combine all locations to process in single loop
                const occupantLocations = showNameonAllUnits
                  ? [mainLocation, ...relatedLocations]
                  : [
                      mainLocation,
                      ...relatedLocationsWithoutSameOrdinalLocations,
                    ]
                const renderType = feature.properties.render_type

                occupantLocations.forEach((location, index) => {
                  const isMainLocation = index === 0

                  // Handle 3D elements (currently only ground labels)
                  if (renderType === "Label") {
                    const occupantGroundLabel = createOccupantGroundLabel(
                      feature,
                      location,
                      { textMarkerType, extrudeConfig },
                      this.threeLayer
                    )

                    // // Add data for ground label
                    occupantGroundLabel.properties = {
                      ...occupantGroundLabel.properties,
                      id: feature.id,
                      feature_type: "occupant",
                      render_type: "Label",
                      originalFeature: feature,
                    }

                    // Add 3D ground label to object3ds
                    if (occupantGroundLabel instanceof GroundLabel) {
                      occupantGroundLabel.on("click", this.handleClickElement)
                      occupantGroundLabel.addTo(this.threeLayer)
                      object3ds.push(occupantGroundLabel)
                      this.#groundObjects.push(occupantGroundLabel)
                    }
                  } else {
                    // Handle 2D markers (UIMarker or regular Marker)
                    const occupantMarker = createOccupant(feature, location, {
                      textMarkerType,
                      extrudeConfig,
                    })
                    /**
                     * !Temporary condition; can be removed later if we implement all markers (logo, logo + name, name) to UIMarker.
                     * UIMarker Can only be added to the map after rc.29
                     * https://github.com/maptalks/maptalks.js/releases/tag/v1.0.0-rc.29
                     */

                    // Add marker to map based on type
                    if (occupantMarker instanceof ui.UIMarker) {
                      occupantMarker.addTo(this.map)
                    } else {
                      occupantMarker?.on("click", this.handleClickElement)
                      occupantMarker?.addTo(layer)
                    }

                    if (isMainLocation) {
                      // Set main location add to elements (so it can be show/hide by ordinal)
                      geometry = occupantMarker
                    } else {
                      // Add related locations to elements (e.g., `occupant-123_unit-123_marker`) so they can be shown/hidden by ordinal
                      const duplexMarkerId = getMarkerId(occupantMarker)
                      elements[duplexMarkerId] = {
                        geometry: occupantMarker,
                        properties: location.properties,
                        featureType: "occupant",
                        feature,
                      }
                    }
                  }
                })
              }
            }
            break
          }
          case "fixture": {
            const models = await create3DFixture(feature, this.threeLayer)
            models.forEach((model) => {
              model.on("click", this.handleClickElement)
              object3ds.push(model)
              this.#glbObjects.push(model)
            })
            if (!featureExtrudeConfig) {
              geometry = createFixture(feature)?.addTo(layer)
            } else {
              const locatedLevel = feature?.properties?.level
              const levelExtrudeConfig = getExtrudeConfigByFeature(
                mergedExtrudeConfig,
                locatedLevel
              )
              const levelHeight = _.get(levelExtrudeConfig, "height", 0)
              const option = { ...featureExtrudeConfig, altitude: levelHeight }

              const extrudedFixture = createExtrudedUnit(
                feature,
                this.threeLayer,
                option
              )

              if (!extrudedFixture) break

              object3ds.push(...extrudedFixture)
            }
            break
          }
          case "kiosk": {
            const models = await create3DKiosk(feature, this.threeLayer)
            models.forEach((model) => {
              model.on("click", this.handleClickElement)
              object3ds.push(model)
              this.#glbObjects.push(model)
            })
            const locatedLevel = feature?.properties?.level
            const levelExtrudeConfig = getExtrudeConfigByFeature(
              mergedExtrudeConfig,
              locatedLevel
            )
            const levelHeight = _.get(levelExtrudeConfig, "height", 0)
            const option = { ...featureExtrudeConfig, altitude: levelHeight }
            const hasAddedModel3d = models?.length > 0

            if (hasAddedModel3d || !featureExtrudeConfig) {
              geometry = createKiosk(feature)
                ?.on("click", this.handleClickElement)
                .addTo(layer)
            } else {
              const extrudedKiosk = createExtrudedKiosk(
                feature,
                this.threeLayer,
                option
              )

              if (!extrudedKiosk) break

              const [mainMesh] = extrudedKiosk
              mainMesh.on("click", this.handleClickElement)
              object3ds.push(...extrudedKiosk)
            }

            break
          }
          case "footprint": {
            // Added 3D Footprint
            const objects = await create3DFootprint(
              feature,
              this.threeLayer,
              featureExtrudeConfig
            )
            objects.forEach((object) => {
              object.on("click", () => {
                const {
                  geometry: { coordinates },
                } = turfCenter(feature)
                const zoom = this.#defaultZoom + 1
                this.flyToAndZoomIn(coordinates, { zoom, pitch: 45 })
              })
              object3ds.push(object)
              this.#objects.push(object)
            })

            // Add Footprint Marker
            if (feature.properties.logo) {
              const footprintMarker = create3DBillboard(
                feature,
                this.threeLayer
              )
              object3ds.push(footprintMarker)
              this.#billboardObjects.push(footprintMarker)
              this.#billboardObjectsById[footprintMarker.properties.id] =
                footprintMarker
            }

            break
          }
          default:
            break
        }
        if (!id) throw new Error(`Property is missing ID, ${id}`)
        elements[id] = { geometry, properties, featureType, feature }
      } catch (err) {
        console.warn(`Cannot create ${feature.id}: ${err.message}`)
      }
    }

    const unitFeatures = this.#features.filter((f) => f.feature_type === "unit")

    this.#groundLabels?.forEach((label) => {
      const text = label.properties.name
      try {
        const ordinal = label.properties.ordinal
        const center = turfCenter({
          type: "Polygon",
          coordinates: label.geometry.coordinates,
        })

        // filter ordinal
        const sameOrdinalUnits = unitFeatures.filter(
          (f) => f.properties.ordinal === ordinal
        )

        const containingUnit = sameOrdinalUnits.find((f) =>
          booleanPointInPolygon(center, f)
        )

        const featureExtrudeConfig = getExtrudeConfigByFeature(
          mergedExtrudeConfig,
          containingUnit
        )
        const extrudeHeight = _.get(featureExtrudeConfig, "height", 0)

        const groundLabel = create3DGroundLabel(
          label,
          this.threeLayer,
          extrudeHeight
        )
        object3ds.push(groundLabel)
        this.#groundObjects.push(groundLabel)
      } catch (error) {
        console.log("error creating ground label for ", text)
      }
    })

    this.#mapDecorations?.forEach((decoration) => {
      const { id, geometry, properties } = decoration
      const { location } = properties || {}
      const geometryType = decoration?.geometry?.type
      const featureExtrudeConfig = getExtrudeConfigByFeature(
        mergedExtrudeConfig,
        location
      )

      const decorationExtrudeConfig = getExtrudeConfigByFeature(
        mergedExtrudeConfig,
        decoration
      )
      let createdDecoration = null
      try {
        switch (geometryType) {
          case "Point":
            const createdBillboard = create3DBillboard(
              decoration,
              this.threeLayer
            )
            this.#billboardObjects.push(createdBillboard)
            this.#billboardObjectsById[createdBillboard.properties.id] =
              createdBillboard

            object3ds.push(createdBillboard)
            break
          default:
            if (!featureExtrudeConfig) {
              const decorationLayer = this.map.getLayer(`base`)
              createdDecoration = createDecoration(geometry, {
                id,
                ...properties,
              }).addTo(decorationLayer)
            } else {
              const option = {
                ...decorationExtrudeConfig,
                bottomHeight: featureExtrudeConfig.height,
              }

              const extrudedDecoration = createExtrudedUnit(
                decoration,
                this.threeLayer,
                option
              )

              if (!extrudedDecoration) break
              object3ds.push(...extrudedDecoration)
            }

            elements[id] = {
              geometry: createdDecoration,
              properties,
              featureType: "decoration",
            }
            break
        }
      } catch (error) {
        console.log(
          "error creating decoration for ",
          decoration.properties.name
        )
      }
    })

    for (const model of this.#model3ds) {
      const modelName = model.properties.name.en
      if (_.isEmpty(model.geometry.coordinates) || model.properties.namespace)
        continue
      try {
        const model3d = await createModel3d(model)

        model3d?.on("click", this.handleClickElement)

        this.#gltfMarkers.push(model3d)
      } catch (error) {
        console.log("error creating model:", modelName, error)
      }
    }

    if (config("escalator3dModelFeatureFlag")) {
      const enableEscalatorAnimation = _.get(
        this.#mapConfig,
        "animations.escalator.enable",
        true
      )
      const escalators = this.#features.filter(
        (f) =>
          f.feature_type === "unit" && f.properties.category === "escalator"
      )
      for (const escalator of escalators) {
        try {
          const relationships = this.#features.filter(
            (f) => f.feature_type === "relationship"
          )
          const openings = this.#features.filter(
            (f) => f.feature_type === "opening"
          )

          const escalatorRelationships = relationships.filter((r) =>
            (r.properties?.intermediary || []).some(
              (inter) => inter.id === escalator.id
            )
          )
          if (escalatorRelationships.length === 0) {
            throw new Error("Cannot find escalator relationship")
          }
          if (escalatorRelationships.length > 1) {
            throw new Error("Found more than one relationship")
          }
          const thisOrdinal = escalator.properties.ordinal
          const relationship = escalatorRelationships[0]
          if (!relationship.properties.origin?.id)
            throw new Error(
              `relationship (id=${relationship.id}) - origin not exists`
            )
          if (!relationship.properties.destination?.id)
            throw new Error(
              `relationship (id=${relationship.id}) - destination not exists`
            )

          const bothOpeningIds = [
            relationship.properties.origin.id,
            relationship.properties.destination.id,
          ]
          const bothOpenings = openings.filter((o) =>
            bothOpeningIds.includes(o.id)
          )
          const thisLevelOpening = bothOpenings.find(
            (opening) => opening.properties.ordinal === thisOrdinal
          )
          const thatLevelOpening = bothOpenings.find(
            (opening) => opening.properties.ordinal !== thisOrdinal
          )
          const angle =
            180 *
            (1 / Math.PI) *
            angleBetweenLineStrings(
              thisLevelOpening.geometry.coordinates,
              thatLevelOpening.geometry.coordinates
            )
          const direction =
            thisOrdinal < thatLevelOpening.properties.ordinal ? "up" : "down"
          const escalatorEntryPoint =
            turfCenter(thisLevelOpening).geometry.coordinates
          const relationshipDirection = relationship.properties.direction
          const model = await create3dEscalator(
            escalator,
            escalatorEntryPoint,
            {
              direction,
              angle,
              relationshipDirection,
              animation: enableEscalatorAnimation,
            }
          )
          this.#gltfMarkers.push(model)
        } catch (error) {
          console.log(`cannot create escalator:`, escalator.id, error)
        }
      }
    }

    // Initiate Canvas render
    this.render()
    // #createElements can re-run (e.g. features/labels/model3ds updating).
    // Objects pushed into #object3ds from outside this method (user-location
    // sprite, navigation geometries) live in the same array and would
    // otherwise be silently dropped by the wholesale assignment below,
    // escaping ordinal filtering and ending up drawn on every floor.
    const isExternallyOwned = (obj) =>
      obj instanceof NavigationPath ||
      ["user-location", "origin-marker"].includes(obj?.properties?.namespace)

    this.#elements = elements
    this.#elementsLoaded = true
    this.#featuresInitted = true
    this.#object3ds = [
      ...this.#object3ds.filter(isExternallyOwned),
      ...object3ds,
    ]

    // #elements was just replaced above; restore the still-live user-location
    // entry so it isn't dropped along with it.
    if (this.#userLocationElement) {
      this.#elements[USER_LOCATION_ELEMENT_ID] = this.#userLocationElement
    }
    if (typeof this.onMapReady === "function") this.onMapReady()
    if (typeof this.onModelReady === "function") this.onModelReady()
  }

  #clearAllElementOnLayerByName = (layerName: string) => {
    const layer = this.map.getLayer(layerName)
    if (layer instanceof VectorLayer) {
      layer.clear()
    }
  }

  #showGeometriesByOrdinal(ordinal: number) {
    _(this.#elements)
      .filter((element) => !!_.get(element, "geometry"))
      .forEach(({ featureType, geometry, properties }) => {
        const show =
          ordinal === null ||
          properties?.ordinal === ordinal ||
          featureType === "venue"

        const layerName = _.get(
          LAYER_FEATURE_TYPE_OBJ,
          featureType,
          featureType
        )
        const markerLayerId = buildCompositeId([featureType], {
          separator: "-",
          suffix: SUFFIX_MARKER,
        })
        const markerLayerName = _.get(LAYER_FEATURE_TYPE_OBJ, markerLayerId)
        const layer = this.map.getLayer(layerName)

        const markerLayer = this.map.getLayer(markerLayerName)

        if (!layer && !markerLayer) return

        if (geometry instanceof ui.UIMarker)
          show ? geometry.addTo(this.map) : geometry.remove()

        if (markerLayer instanceof VectorLayer && geometry instanceof Marker)
          show
            ? markerLayer.addGeometry(geometry)
            : markerLayer.removeGeometry(geometry)

        if (layer instanceof VectorLayer && geometry instanceof Geometry)
          show ? layer.addGeometry(geometry) : layer.removeGeometry(geometry)

        //! Prevent animating geometry from stopping after being removed and re-added to the layer.
        if (geometry._animPlayer instanceof animation.Player) {
          const animPlayer = geometry?._animPlayer
          show ? animPlayer.play() : animPlayer.cancel()
        }
      })
  }

  #showThreeObjectByOrdinal = (ordinal: number) => {
    const threeLayer = this.threeLayer
    const objectsToAdd = []
    const objects = this.#object3ds || []
    const isOriginMarkerSpriteShowing = objects.some(
      (obj) => obj.properties?.namespace === "origin-marker"
    )
    objects?.forEach((baseObject) => {
      const objectOrdinal = _.get(baseObject, "properties.ordinal")
      const featureType = _.get(baseObject, "properties.feature_type")
      const isAdd = _.get(baseObject, "isAdd")
      const isUserLocationHiddenByOriginMarker =
        baseObject.properties?.namespace === "user-location" &&
        isOriginMarkerSpriteShowing
      const show =
        !isUserLocationHiddenByOriginMarker &&
        (Boolean(objectOrdinal === ordinal) ||
          ALWAYS_VISIBLE_FEATURE_TYPES.includes(featureType))
      if (show) return objectsToAdd.push(baseObject)
      if (isAdd) return threeLayer.removeMesh(baseObject)
    })
    threeLayer.addMesh(objectsToAdd)
  }

  #showGltfMarkerByOrdinal = (ordinal: number) => {
    const gltfLayer = this.gltfLayer
    const markersToAdd = []
    const markers = this.#gltfMarkers || []
    markers?.forEach((marker) => {
      const objectOrdinal = _.get(marker, "properties.ordinal")
      //show marker that on the same floor as current floor or marker that doesn't have ordinal
      const show =
        Boolean(objectOrdinal === ordinal) || !_.isNumber(objectOrdinal)
      const isAdd = _.get(marker, "properties.isAdd")
      if (isAdd && !show) {
        gltfLayer.removeGeometry(marker)
        marker.properties.isAdd = false
      }
      if (show) {
        markersToAdd.push(marker)
        marker.properties.isAdd = true
      }
    })
    gltfLayer.addGeometry(markersToAdd)
  }

  /**
   * Change Level & animate to path / geometry / view / etc.
   * ================================== */
  changeLevelByOrdinal(ordinal: number): void {
    this.#currentOrdinal = ordinal
    this.#showGeometriesByOrdinal(ordinal) // show / hide geometry on the ordinal
    this.#showThreeObjectByOrdinal(ordinal)
    this.#showGltfMarkerByOrdinal(ordinal)
    this.reapplyPersistentHighlight()
  }

  #animateflyTo(viewOptions = {}, options = {}, callbackOptions = {}) {
    // Marks this fly as programmatic so moveend-driven boundary limiting
    // (useLimitMapView) doesn't fight it mid-animation; cleared once it settles.
    this.#isProgrammaticFlying = true

    const { start, end } = {
      start: (frame) => {},
      end: (frame) => {},
      ...callbackOptions,
    }

    this.map.animateTo(viewOptions, options, (frame) => {
      if (frame.state.playState === "running" && frame.state.progress === 0)
        start(frame)
      if (frame.state.playState === "finished") {
        this.#isProgrammaticFlying = false
        end(frame)
      }
    })
  }

  isProgrammaticFlying = () => this.#isProgrammaticFlying

  getBearing = () => this.map.getBearing()
  getPitch = () => this.map.getPitch()
  getZoom = () => this.map.getZoom()
  setBearing = (bearing) => this.map.setBearing(bearing)

  getFeatureExtent = (feature: any, scaleFactor = 1) => {
    const featureBbox = bbox(
      scale(bboxPolygon(bbox(feature)), scaleFactor)
    ) as [number, number, number, number]

    return new Extent(featureBbox)
  }

  getExtentCenter = (extent: Extent) => {
    return extent.getCenter()
  }

  getExtentZoom = (
    extent: Extent,
    options: {
      isFraction?: boolean
      padding?: {
        paddingLeft: number
        paddingRight: number
        paddingTop: number
        paddingBottom: number
      }
    } = {
      isFraction: false,
      padding: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
      },
    }
  ) => {
    const { isFraction = false, padding } = options
    return this.map.getFitZoom(extent, isFraction, padding)
  }

  findVenueInView = (): {
    venueId: string
    distance: number
    venue: ImdfFeature
  } => {
    const mapCenter = this.map.getCenter()
    const result = this.#venues.reduce<{
      venueId: string
      distance: number
      venue: ImdfFeature
    } | null>((closest, venue) => {
      const { display_point: displayPoint } = venue.properties
      const distance = turfDistance(displayPoint, [mapCenter.x, mapCenter.y])

      if (!closest || distance < closest.distance) {
        return { venueId: venue.id, distance, venue }
      }

      return closest
    }, null)

    return result
  }

  flyTo = (
    center,
    options: IFlyToMapOptions = {
      zoom: this.#defaultZoom,
      pitch: 60,
      duration: 600,
      delay: 2000,
      ease: "out",
      bearing: this.getBearing(),
    }
  ) => {
    const {
      zoom = this.#defaultZoom,
      pitch = 60,
      duration = 600,
      delay = 2000,
      ease = "out",
      bearing = this.getBearing(),
    } = options
    this.#animateflyTo(
      {
        center,
        zoom,
        pitch,
        bearing,
      },
      { delay, duration, ease }
    )
  }
  getLineStringBearing = (feature) => {
    const { geometry } = feature
    const path = new LineString(geometry.coordinates)
    return getBearingBetweenPoints(
      path.getFirstCoordinate(),
      path.getLastCoordinate()
    )
  }

  //map animation
  addAnimations(animation: { id: string; callback: () => void }) {
    this.#animationsToRun.push(animation)
  }

  removeAnimationById(id: string) {
    this.#animationsToRun = this.#animationsToRun.filter(
      (animation) => animation.id !== id
    )
  }

  clearAnimations() {
    this.#animationsToRun = []
  }
  // Subject to be removed
  flyToAndZoomIn = (
    centerPoint,
    options: IFlyToMapOptions = {
      zoom: this.#defaultZoom + 3,
      pitch: 60,
      duration: 600,
      delay: 2000,
      ease: "out",
    }
  ) => {
    const {
      zoom = this.#defaultZoom + 3,
      pitch = 60,
      duration = 600,
      delay = 2000,
      ease = "out",
      bearing,
    } = options
    this.#animateflyTo(
      {
        center: centerPoint,
        zoom,
        pitch,
        // Only rotate when the caller actually knows a target bearing (e.g.
        // the last step's walking direction) — omitted (as for vertical
        // steps) leaves the camera's current bearing untouched.
        ...(bearing !== undefined && { bearing }),
      },
      { delay, duration, ease }
    )
  }

  /**
   * Hilighting Elements
   * ========================================= */
  // TODO: To consider if we should use setter `set hilightElementIds` instead?
  setHighlightElementIds(
    targetElementIds,
    options: ISetHighlightElementIdsOptions = {}
  ) {
    const highlight3DOptions = _.merge(
      {},
      DEFAULT_HIGHLIGHT_OPTIONS,
      _.get(options, "highlight3DOptions", {})
    )
    const highlight2DOptions = _.merge(
      {},
      DEFAULT_SET_HIGHLIGHT_2DELEMENT_IDS_OPTIONS,
      _.get(options, "highlight2DOptions", {})
    )

    this.setHighlightedObject(targetElementIds, highlight3DOptions)
    return this.setHighlight2DElementIds(targetElementIds, highlight2DOptions)
  }
  setHighlight2DElementIds(targetElementIds, options = {}) {
    const { defaultMarker, symbolSet } = _.merge(
      {},
      DEFAULT_SET_HIGHLIGHT_2DELEMENT_IDS_OPTIONS,
      options
    )
    const {
      createMarker,
      createHighlightOccupantMarker,
      createHighlightSectionMarker,
      getElementSymbol,
      getHilighPolygonalSymbol,
      getHighlightMarkerSymbol,
    } = this.#styler
    const extrudeConfig = _.get(this.#mapConfig, "extrude")

    /**
     * Hilight new elements
     */
    const elementToHilights = targetElementIds
      .map(
        (elemId) =>
          this.#elements[elemId] ||
          this.#elements[`${LAST_USER_LOCATION_ELEMENT_ID_PREFIX}${elemId}`]
      )
      .filter((elem) => elem)

    elementToHilights.forEach((elem) => {
      const { feature, geometry } = elem
      if (!geometry || !feature) return

      const hilightLayer = this.map.getLayer(HIGHLIGHT_LAYER_NAME)
      if (!hilightLayer) return

      const defaultSymbol = getHilighPolygonalSymbol(geometry.type)
      const definedSymbol = symbolSet ? getElementSymbol(symbolSet) : null
      const symbol = _.isEmpty(definedSymbol) ? defaultSymbol : definedSymbol

      switch (geometry.type) {
        case "MultiPolygon":
        case "Polygon": {
          const highlightPolygonMode = _.get(
            this.#mapConfig,
            "highlight_polygon_mode"
          )
          const highlightPolygonColorRatio = _.get(
            this.#mapConfig,
            "highlight_polygon_color_ratio",
            0.25
          ) as number
          const { polygonFill, ...rest } = symbol
          const defaultPolygonFill = geometry.options.defaultSymbol.polygonFill
          const generatedColor = Color(defaultPolygonFill)
            .darken(highlightPolygonColorRatio)
            .hex()
          const color =
            highlightPolygonMode === "auto" ? generatedColor : polygonFill
          geometry?.updateSymbol({ ...rest, polygonFill: color })
          break
        }
        default:
          break
      }

      switch (feature.feature_type) {
        case "amenity":
          const highlightedAmenityMarker =
            definedSymbol || getHighlightMarkerSymbol()
          geometry?.updateSymbol(highlightedAmenityMarker)
          break
        case "occupant": {
          switch (feature.properties.category) {
            case "currencyexchange":
            case "donationcenter":
            case "postoffice":
              const highlightedAmenityMarker =
                definedSymbol || getHighlightMarkerSymbol()
              geometry?.updateSymbol(highlightedAmenityMarker)
              break
            default:
              if (feature.properties.render_type === "Logo") {
                this.hideGeometryByElementId(geometry?.properties?.id)
              }
              createHighlightOccupantMarker(feature, {
                extrudeConfig,
                symbol: definedSymbol,
              })
                .on("click", this.handleClickElement)
                .addTo(hilightLayer)
              break
          }
          break
        }
        case "section": {
          if (feature.properties.render_type === "Logo") {
            this.hideGeometryByElementId(geometry.properties.id)
          }

          createHighlightSectionMarker(feature, {
            extrudeConfig,
            symbol: definedSymbol,
          })
            .on("click", this.handleClickElement)
            .addTo(hilightLayer)
          break
        }
        case "opening":
          break
        default:
          if (defaultMarker) createMarker(feature).addTo(hilightLayer)
          break
      }

      /* Hide the occupant marker before display the highlight marker */
      // const occupantId = IndexedOccupantMarkers[feature.id]
      // if (occupantId) {
      //   hideOccupantMarker(occupantId)
      // }
    })

    // Store new highlightIds to prev ref.
    this.#highlightElementIds = targetElementIds

    if (elementToHilights.length === 0) return
    return featureCollection(
      elementToHilights
        .map(({ feature }) => {
          const { geometry } = feature
          // TODO: consider removing this logic later
          // Skip retrieving features for occupants without an anchor binding
          if (feature.feature_type === "occupant") {
            return feature?.properties?.anchor
              ? turfFeature(feature?.properties?.anchor?.geometry)
              : null
          }
          return turfFeature(geometry)
        })
        .filter(Boolean)
    )
  }

  clearHighlightElements() {
    /**
     * Clear previous highlights
     */
    this.#clearAllElementOnLayerByName(HIGHLIGHT_LAYER_NAME)
    //Return geometry to defaultSymbol
    _(this.#highlightElementIds)
      .map((elemId) => {
        const feature = this.#elements[elemId]?.feature
        const geometry = this.#elements[elemId]?.geometry
        if (!geometry) return null

        return {
          feature,
          geometry,
        }
      })
      .compact()
      .forEach(({ feature, geometry }) => {
        if (geometry instanceof ui.UIMarker) return

        if (geometry instanceof Marker) {
          this.showGeometryByElementId(geometry.properties.id)
          return
        }

        try {
          const defaultSymbol = geometry.options.defaultSymbol
          geometry.updateSymbol(defaultSymbol)
        } catch (err) {
          console.log(
            `error cannot return to defaultSymbol, check if "defaultSymbol" exists in element creation function`
          )
        }
      })
    this.#highlightElementIds = []
    // Does not restore persistent highlight. Call reapplyPersistentHighlight() afterward if needed.
  }

  setPersistentHighlightElementIds(
    targetElementIds: string[],
    options: ISetHighlightElementIdsOptions = {}
  ) {
    this.#persistentHighlightElementIds = targetElementIds
    this.#persistentHighlightOptions = options
    this.reapplyPersistentHighlight()
  }

  clearPersistentHighlightElementIds() {
    this.#persistentHighlightElementIds = []
    this.#persistentHighlightOptions = {}
    this.clearHighlightElements()
    this.clearHighlightObject()
  }

  reapplyPersistentHighlight() {
    if (this.#persistentHighlightElementIds.length === 0) return
    const highlight3DOptions = _.merge(
      {},
      DEFAULT_HIGHLIGHT_OPTIONS,
      _.get(this.#persistentHighlightOptions, "highlight3DOptions", {})
    )
    const highlight2DOptions = _.merge(
      {},
      DEFAULT_SET_HIGHLIGHT_2DELEMENT_IDS_OPTIONS,
      _.get(this.#persistentHighlightOptions, "highlight2DOptions", {})
    )
    this.setHighlightedObject(
      this.#persistentHighlightElementIds,
      highlight3DOptions
    )
    this.setHighlight2DElementIds(
      this.#persistentHighlightElementIds,
      highlight2DOptions
    )
  }

  setHighlightedObject(targetObjectIds, options = {}) {
    const { symbolSet } = _.merge({}, DEFAULT_HIGHLIGHT_OPTIONS, options)
    const {
      getElementSymbol,
      getHilighPolygonalSymbol,
      createHighlight2DAmenityMarkerFrom3DMarker,
      createHighlightOccupantMarker,
    } = this.#styler
    const objects = this.threeLayer?.getBaseObjects()
    const objectsToHighlight = objects.filter(({ properties }) =>
      targetObjectIds.includes(properties?.id)
    )

    const defaultSymbol = getHilighPolygonalSymbol("Polygon")
    const targetSymbol = symbolSet ? getElementSymbol(symbolSet) : null
    const { polygonFill } = _.isEmpty(targetSymbol)
      ? defaultSymbol
      : targetSymbol

    const amenityHighlightMode = _.get(
      this.#mapConfig,
      "amenity_highlight_mode",
      ""
    )

    const extrudeConfig = _.get(this.#mapConfig, "extrude")

    objectsToHighlight.forEach((obj) => {
      if (obj.properties.feature_type === "section") {
        const newController = createHighlightSectionVisibilityController(obj)

        newController.start()

        this.#highlightObjectControllers.push(newController)
        this.#highlightObjectIds = targetObjectIds
      }

      //highlight extruded polygon
      if (obj.type === "ExtrudePolygon") {
        const highlightPolygonMode = _.get(
          this.#mapConfig,
          "highlight_polygon_mode"
        )
        const highlightPolygonColorRatio = _.get(
          this.#mapConfig,
          "highlight_polygon_color_ratio",
          0.25
        ) as number
        const defaultPolygonFill = obj.properties.defaultColor
        const generatedColor = Color(defaultPolygonFill)
          .darken(highlightPolygonColorRatio)
          .hex()

        const color =
          highlightPolygonMode === "auto" ? generatedColor : polygonFill
        const newController = createHighlightExtrudeObjectController(obj, {
          color,
        })

        newController.start()
        this.#highlightObjectControllers.push(newController)
      }
      //highlight amenity marker
      if (obj instanceof SpriteMarker) {
        if (amenityHighlightMode === "2DMarker") {
          const highlight2DLayer = this.map.getLayer(HIGHLIGHT_LAYER_NAME)
          const extrudeConfig = _.get(this.#mapConfig, "extrude")
          obj.hide()
          const { properties: featureProperties } = obj
          const highlight2DMarker = createHighlight2DAmenityMarkerFrom3DMarker(
            featureProperties,
            extrudeConfig
          )
            .on("click", this.handleClickElement)
            .addTo(highlight2DLayer)
          this.#highlight2DMarkers.push(highlight2DMarker)
        } else {
          obj.highlight()
        }
      }

      if (obj instanceof Billboard) {
        const newController = createHighlightBillboardController(obj)
        newController.start()
        this.#highlightObjectControllers.push(newController)
      }

      // Handle case occupant groundLabel
      if (obj instanceof GroundLabel) {
        const highlight2DLayer = this.map.getLayer(HIGHLIGHT_LAYER_NAME)
        const { properties: objProperties } = obj

        //Find Occupant that has render type is Label
        const occupantFeature = this.#features.find(
          (feature) =>
            feature.id === objProperties.id &&
            feature.feature_type === "occupant" &&
            feature.properties.render_type === "Label"
        )

        if (occupantFeature) {
          //create 2D highlight occupant marker
          const highlightMarker = createHighlightOccupantMarker(
            occupantFeature,
            {
              extrudeConfig,
              symbol: targetSymbol,
            }
          )

          // if 2D occupant marker created add to highlight layer
          if (highlightMarker) {
            highlightMarker
              .on("click", this.handleClickElement)
              .addTo(highlight2DLayer)
            this.#highlight2DMarkers.push(highlightMarker)
          }
        }
      }
    })

    this.#highlightObjectIds = targetObjectIds
  }

  getBillboardDisplayOffsetByFeatureId(featureId: string): {
    altitude: number
    zoom: number
  } | null {
    const billboard = _.get(
      this.#billboardObjectsById[featureId] || {},
      "properties.displayOffset",
      null
    )

    return billboard
  }

  clearHighlightObject() {
    this.#highlightObjectControllers.forEach((controller) => {
      if (_.isFunction(controller?.clear)) controller.clear()
    })
    this.#highlightObjectIds.forEach((objIds) => {
      const objects = this.threeLayer?.getBaseObjects()
      const objectsToResetHighlight = objects.filter(({ properties }) =>
        objIds.includes(properties?.id)
      )
      objectsToResetHighlight.forEach((obj) => {
        if (obj instanceof SpriteMarker) {
          obj.show()
          obj.removeHighlight()
        }
      })
    })
    // These are created fresh per highlight (setHighlightedObject) and never
    // reused, so leaving them on the layer after a highlight ends piles up
    // one orphaned 2D marker per highlight for the rest of the session.
    this.#highlight2DMarkers.forEach((marker) => marker.remove())

    // Clear the highlight object controllers
    this.#highlightObjectControllers = []
    this.#highlightObjectIds = []
    this.#highlight2DMarkers = []
    // Does not restore persistent highlight. Call reapplyPersistentHighlight() afterward if needed.
  }

  /**
   * User Location
   ****************************/
  // TODO: To consider if we should use setter `set userLocation` instead?

  getDirectionalSpriteUrls() {
    const configuredUrls: any = _.get(this.#mapConfig, "sprite_url")

    const hasIdleUrls = Object.values(configuredUrls?.idle ?? {}).some(Boolean)
    if (!hasIdleUrls) return null

    return {
      idle: configuredUrls.idle,
      walk: configuredUrls.walk ?? configuredUrls.idle,
    }
  }

  // True only when the origin marker will actually render as a sprite (mode
  // requested AND there are usable images) — used to gate hiding the real
  // user-location marker during route playback, since that only makes
  // visual sense when a sprite is actually walking in its place.
  #isOriginMarkerSpriteModeActive() {
    return (
      _.get(this.#mapConfig, "origin_marker_mode") === "sprite-marker" &&
      this.getDirectionalSpriteUrls() !== null
    )
  }

  addUserLocation(value) {
    const {
      createUserLocationMarker,
      createUserLocationUIMarker,
      createDirectionalUserLocationMarkerSprite,
    } = this.#styler

    const { properties, feature_type } = value

    const markerLayer = this.map.getLayer(USER_LOCATION_LAYER_NAME)
    const spriteUrls = this.getDirectionalSpriteUrls()
    const userLocationMarkerMode = _.get(
      this.#mapConfig,
      "user_location_marker_mode"
    )
    // If UserLocationGeometry not exists, create one.
    if (!this.#userLocationElement?.geometry) {
      let geometry = null
      switch (userLocationMarkerMode) {
        case "ui-marker":
          geometry = createUserLocationUIMarker(value)
          geometry.addTo(this.map)
          break
        case "sprite-marker":
          // Facing is derived per-frame in render() — see #syncSpriteFacing.
          geometry = createDirectionalUserLocationMarkerSprite(
            value,
            this.threeLayer,
            {
              scale: _.get(this.#mapConfig, "user_location_sprite_scale"),
              spriteUrls,
            }
          )
          this.#object3ds.push(geometry)
          break
        case "default":
        default:
          if (markerLayer) {
            geometry = createUserLocationMarker(value)
            geometry.addTo(markerLayer)
          }
          break
      }
      // Add marker to this.#elements
      if (geometry) {
        const element = {
          geometry,
          properties,
          featureType: feature_type,
          feature: value,
        }
        this.#elements[USER_LOCATION_ELEMENT_ID] = element
        this.#userLocationElement = element
        this.updateUserLocationSymbolByLocale(this.locale)
        // All three modes add their geometry to the scene/map regardless of
        // floor (markerLayer / this.map / the sprite factory in
        // createElements.js all add unconditionally). Re-run both floor
        // filters so a marker created while another level is displayed can't
        // appear on it — #showGeometriesByOrdinal governs default/ui-marker,
        // #showThreeObjectByOrdinal governs sprite-marker.
        if (this.#currentOrdinal !== null) {
          this.#showGeometriesByOrdinal(this.#currentOrdinal)
          this.#showThreeObjectByOrdinal(this.#currentOrdinal)
        }
      }
    }
  }

  updateUserLocationSymbolByLocale(locale: string) {
    const userLocationMarkerMode = _.get(
      this.#mapConfig,
      "user_location_marker_mode"
    )
    const userLocationGeometry = _.get(
      this.#elements,
      `${USER_LOCATION_ELEMENT_ID}.geometry`
    )

    if (!userLocationGeometry) return

    switch (userLocationMarkerMode) {
      case "ui-marker":
        /**
         * Localized user location marker example.
         * Supports locale-based marker files.
         *
         * Example:
         * {
         *   "symbol": {background:"url(marker.gif)"}, // Default marker
         *   "locale_symbol": { "th": {background:"url(marker_th.gif)"}, "default": { background:"url(marker.gif)" } }
         * }
         */
        const symbol = userLocationGeometry.options.properties.symbol
        const localeSymbol =
          _.get(
            userLocationGeometry.options.properties.locale_symbol,
            locale
          ) ||
          _.get(
            userLocationGeometry.options.properties.locale_symbol,
            "default"
          )
        userLocationGeometry.setContent(
          createStyledUIMarkerElement({
            style: { ...symbol, ...localeSymbol },
            textContent: null,
            className: "user-location",
          })
        )
        break
      case "sprite-marker":
        break
      case "default":
      default:
        const currentSymbol = userLocationGeometry.getSymbol()

        /**
         * Localized user location marker example.
         * Supports locale-based marker files.
         *
         * Example:
         * {
         *   "markerFile": "logo_url", // Default marker
         *   "locale_symbol": { "th": { "markerFile": "logo_url" }, "default": { "markerFile": "logo_url" } }
         * }
         */
        const localeSymbolToUpdate = currentSymbol.map((symbol) => {
          const localeSymbol =
            _.get(symbol, `${LOCALE_SYMBOL_KEY}.${locale}`) ||
            _.get(symbol, `${LOCALE_SYMBOL_KEY}.default`)
          if (!_.isPlainObject(localeSymbol)) return symbol

          return {
            ...symbol,
            ...localeSymbol,
          }
        })
        userLocationGeometry?.updateSymbol(localeSymbolToUpdate)
    }
  }

  // use when using animated origin-marker and it's the same model as you are here marker
  hideUserLocation3dModel() {
    const userLocationMarker = (this.#object3ds ?? []).find(
      (marker) => marker?.properties?.namespace === "user-location"
    )
    if (userLocationMarker && userLocationMarker?.isAdd) {
      this.threeLayer.removeMesh(userLocationMarker)
    }
  }
  showUserLocation3dModel() {
    // Visibility is derived, not commanded. clearNavigationGeometries() can run
    // after the level has already been applied (usePlaceDetailMap does exactly
    // that), so re-run the one floor filter instead of adding the mesh blind —
    // an unconditional addMesh draws the sprite on whatever floor is displayed.
    //
    // The null check is LOAD-BEARING, not defensive: #showThreeObjectByOrdinal(null)
    // does not mean "no filter" — it removes every object carrying an ordinal,
    // leaving only venue/footprint. And #currentOrdinal is still null on first
    // mount, where useOrdinalMap calls clearNavigationGeometries() before
    // changeLevelByOrdinal().
    if (this.#currentOrdinal !== null)
      this.#showThreeObjectByOrdinal(this.#currentOrdinal)
  }

  removeUserLocation() {
    if (this.#userLocationElement) {
      const { geometry } = this.#userLocationElement
      if (geometry) geometry.remove()
      this.hideUserLocation3dModel()
      this.#object3ds = (this.#object3ds ?? []).filter(
        (obj) => obj?.properties?.namespace !== "user-location"
      )
      this.#userLocationElement = null
      this.#elements[USER_LOCATION_ELEMENT_ID] = null
    }
  }

  addLastUserLocation(value) {
    const newLastLocationId = value?.id
    // If the last location ID is undefined or exists, no new marker will be added.
    const isExists =
      this.#lastUserLocationGeometries.findIndex(
        ({ feature }) => feature?.id === newLastLocationId
      ) >= 0

    if (!newLastLocationId || isExists) return

    const { createLastUserLocationMarker } = this.#styler

    this.#lastUserLocationGeometries = [
      ...this.#lastUserLocationGeometries,
      value,
    ]

    const { properties, feature_type, id } = value

    const markerLayer = this.map.getLayer(USER_LOCATION_LAYER_NAME)

    const geometry = createLastUserLocationMarker(value)
    if (!geometry) return
    geometry.addTo(markerLayer)

    // Add marker to this.#elements
    const element = {
      geometry,
      properties,
      featureType: feature_type,
      feature: value,
    }
    this.#elements[`${LAST_USER_LOCATION_ELEMENT_ID_PREFIX}${id}`] = element
    this.#lastUserLocationElements = [
      ...this.#lastUserLocationElements,
      element,
    ]
  }

  removeLastUserLocation() {
    this.#lastUserLocationGeometries = []
    if (
      this.#lastUserLocationElements &&
      this.#lastUserLocationElements.length > 0
    ) {
      for (const lastUserLocationElem of this.#lastUserLocationElements) {
        const { geometry, properties = {} } = lastUserLocationElem
        if (geometry) {
          geometry.remove()
          delete this.#elements[`last_user_location-${properties?.id}`]
        }
      }
      this.#lastUserLocationElements = []
    }
  }

  hideLastUserLocationMarker() {
    for (const lastUserLocationElem of this.#lastUserLocationElements) {
      const { geometry } = lastUserLocationElem
      if (geometry) geometry.hide()
    }
  }

  /**
   * END of User Location
   ****************************/

  showGeometryByElementId = (elementId): void => {
    const geometry: Geometry | undefined = _.get(
      this.#elements,
      `${elementId}.geometry`
    )
    if (geometry) geometry.show()
  }

  hideGeometryByElementId = (elementId): void => {
    const geometry: Geometry = _.get(this.#elements, `${elementId}.geometry`)
    if (geometry) geometry.hide()
  }

  hideGeometryByElementIds = (elementIds = []): void => {
    elementIds.forEach((id) => this.hideGeometryByElementId(id))
  }

  setSpriteMarkersOpacity = (opacity: number = 1): void => {
    const spriteMarkerObjects = this.#spriteMarkerObjects
    spriteMarkerObjects?.forEach((baseObject) => {
      baseObject.getObject3d().traverse((child) => {
        if (child.isLine || child.isSprite) {
          child.material.opacity = opacity
        }
      })
    })
  }

  setFeatureObject3DsOpacity = (opacity: number = 1): void => {
    const objects = this.#object3ds

    objects?.forEach((baseObject) => {
      const featureType = _.get(baseObject, "properties.feature_type")
      if (
        baseObject instanceof NavigationPath ||
        PRESERVE_OPACITY_FEATURE_TYPES.includes(featureType)
      )
        return

      baseObject.getObject3d().traverse((child) => {
        if (child.isMesh === true) {
          child.material.opacity = opacity
        }
      })
    })
  }

  /*
   * Enable or disable the fading effect based on the zoom level for all layers except the Venue Model.
   * This function applies when extruded units exceed the edges of the Venue Model while zooming out.
   * It can be enabled only when the Venue Model is present.
   */
  disableLayersFadingOnZoom = (): void => {
    this.#isLayersFadingOnZoom = false
  }

  enableLayersFadingOnZoom = (): void => {
    this.#isLayersFadingOnZoom = this.#venueMarkers.length !== 0
  }

  /**
   * Navigation
   ****************************/
  combineNearbyLineStrings(
    lineStrings: Feature<LineStringType, GeoJsonProperties>[],
    options: {
      properties?: GeoJsonProperties
      distance?: number
    } = { properties: {}, distance: 0.0003 }
    // 0.0003 = 30cm
  ) {
    const { properties = {}, distance = 0.0003 } = options || {}
    const combinedLineStrings = []
    const accLine = []
    if (lineStrings.length === 1) return lineStrings

    for (let i = 0; i < lineStrings.length; i++) {
      const line = lineStrings[i]
      const coords = line.geometry.coordinates
      const prevLine = lineStrings[i - 1]
      const firstCoord = _.first(coords)
      const isFirstLine = i === 0

      if (isFirstLine) {
        accLine.push(...coords)
        continue
      }

      const prevLastCoord = _.last(prevLine.geometry.coordinates)
      const isNearby =
        turfDistance(point(firstCoord), point(prevLastCoord)) < distance

      if (!isNearby) {
        const remainingLines = lineStrings.slice(i)
        const res = this.combineNearbyLineStrings(remainingLines, {
          properties,
        })
        combinedLineStrings.push(...res)
        break
      }
      accLine.push(...coords)
    }
    combinedLineStrings.push(lineString(accLine, properties))
    return combinedLineStrings
  }

  createNavigationGeometries = (stepGeometries, destinationFeature) => {
    const {
      createOriginMarker,
      createDestinationPinMarker,
      createDestinationLogoMarker,
      create3DStepPath,
      createDirectionalOriginMarkerSprite,
    } = this.#styler
    const routeMarkerLayer = this.map.getLayer(HIGHLIGHT_LAYER_NAME)

    const linesByOrdinal = _(stepGeometries)
      .filter(({ geometry }) => geometry.type === "LineString")
      .groupBy("properties.ordinal")
      .value()

    const joinedLines = _(linesByOrdinal).reduce((acc, lines, key) => {
      const joined = this.combineNearbyLineStrings(lines, {
        properties: { ordinal: +key },
      })
      return [...acc, ...joined]
    }, [])

    joinedLines.forEach((line, index) => {
      try {
        const navPath: NavigationPath = create3DStepPath(line, this.threeLayer)
        this.threeLayer.addMesh([navPath])
        this.#navigationGeometries[`line-${index}`] = navPath
        this.#object3ds.push(navPath)
      } catch (err) {
        console.log(err)
      }
    })

    stepGeometries.forEach((stepGeometry) => {
      const { geometry, properties, feature_type = null } = stepGeometry
      let stepElement
      try {
        switch (geometry.type) {
          // Create a destination marker and route path
          case "Point":
            switch (feature_type) {
              case "origin-marker": {
                const spriteUrls = this.getDirectionalSpriteUrls()

                // No usable sprite images at all — fall back to the regular
                // marker rather than rendering an invisible one.
                if (
                  _.get(this.#mapConfig, "origin_marker_mode") ===
                    "sprite-marker" &&
                  spriteUrls !== null
                ) {
                  // The origin's floor can be unknown (geolocation origin
                  // with no level). Seed it from the floor on display so the
                  // sprite is governed by the same ordinal filter as every
                  // other object3d rather than needing an exemption.
                  const originOrdinal = _.isFinite(properties?.ordinal)
                    ? properties.ordinal
                    : this.#currentOrdinal
                  const spriteMarker = createDirectionalOriginMarkerSprite(
                    {
                      ...stepGeometry,
                      properties: { ...properties, ordinal: originOrdinal },
                    },
                    this.threeLayer,
                    {
                      scale: _.get(
                        this.#mapConfig,
                        "origin_marker_sprite_scale"
                      ),
                      spriteUrls,
                    }
                  )
                  this.#object3ds.push(spriteMarker)
                  break
                }

                stepElement =
                  createOriginMarker(stepGeometry).addTo(routeMarkerLayer)
                break
              }
              case "destination-marker":
                const extrudeConfig = _.get(this.#mapConfig, "extrude")
                if (destinationFeature.feature_type === "occupant") {
                  const stepId = _.get(stepGeometry, "id")
                  // !Update step ID to occupant destination feature ID to prevent unexpected clearing of occupant element from map
                  const normalizedDestinationFeature = {
                    ...destinationFeature,
                    id: stepId,
                  }
                  const logoUrl = _.get(
                    normalizedDestinationFeature,
                    "properties.logo.url"
                  )
                  const createOccupantDestinationMarkerFn = logoUrl
                    ? createDestinationLogoMarker
                    : createDestinationPinMarker
                  stepElement = createOccupantDestinationMarkerFn(
                    normalizedDestinationFeature,
                    { extrudeConfig }
                  ).addTo(routeMarkerLayer)
                  break
                }
                stepElement = createDestinationPinMarker(destinationFeature, {
                  extrudeConfig,
                }).addTo(routeMarkerLayer)
                break
              default:
                this.#navigationGeometries[stepGeometry.id] = stepElement
                break
            }
            break
          default:
            break
        }
        if (stepElement) {
          const { id, feature_type: featureType } = stepGeometry
          // Add tot Elements object
          this.#elements[id] = {
            geometry: stepElement,
            featureType,
            properties: { ...stepGeometry, ...properties },
          }
          //
        }
      } catch (err) {
        // console.log(err, {
        //   index: index,
        //   stepGeometries,
        // })
        // throw err;
        console.log(err)
      }
    })
    if (this.#isOriginMarkerSpriteModeActive()) {
      this.hideUserLocation3dModel()
    }
  }

  createOverviewStepPathByOrdinal = (stepGeometries, viewingOrdinal) => {
    const { createLineStringFromGeometries } = this.#styler
    const initialStepGeometries = stepGeometries
      .filter(({ properties }) => properties.ordinal === viewingOrdinal)
      .map(({ geometry }) => geometry)
    return createLineStringFromGeometries(initialStepGeometries)
  }

  // Hides the destination marker on the final step when the origin marker is a sprite
  setDestinationMarkerVisible(visible: boolean): void {
    const destinationMarkerElement = this.#elements[DESTINATION_MARKER_ID]
    const destinationMarkerGeometry = destinationMarkerElement?.geometry
    if (!destinationMarkerGeometry) return

    if (!visible) {
      destinationMarkerGeometry.remove()
      return
    }
    const { properties, featureType } = destinationMarkerElement
    const isEligibleForCurrentOrdinal =
      this.#currentOrdinal === null ||
      properties?.ordinal === this.#currentOrdinal ||
      featureType === "venue"
    if (!isEligibleForCurrentOrdinal) return

    const routeMarkerLayer = this.map.getLayer(HIGHLIGHT_LAYER_NAME)
    if (routeMarkerLayer instanceof VectorLayer) {
      routeMarkerLayer.addGeometry(destinationMarkerGeometry)
    }
  }

  clearNavigationGeometries(): void {
    // Remove origin and destination markers from the highlight layer
    const routeMarkerLayer = this.map.getLayer(HIGHLIGHT_LAYER_NAME)
    const originMarkerGeometry = _.get(
      this.#elements,
      `${ORIGIN_MARKER_ID}.geometry`
    )
    const destinationMarkerGeometry = _.get(
      this.#elements,
      `${DESTINATION_MARKER_ID}.geometry`
    )
    const geometriesToRemove = _.compact([
      originMarkerGeometry,
      destinationMarkerGeometry,
    ])

    if (routeMarkerLayer instanceof VectorLayer)
      routeMarkerLayer.removeGeometry(geometriesToRemove)

    this.#elements[ORIGIN_MARKER_ID] = null
    this.#elements[DESTINATION_MARKER_ID] = null

    // Filter out NavigationPath objects from the global this.#object3ds list
    this.#object3ds = this.#object3ds.filter(
      (obj) => !(obj instanceof NavigationPath)
    )

    // Remove related navigation geometry objects
    const objects = this.#navigationGeometries || {}
    _.forEach(objects, (obj) => {
      if (!obj) return
      this.#navigationGeometries[obj.properties.id] = null
      obj.dispose?.()
      obj.remove()
    })

    //clear 3d model origin marker
    this.#object3ds = this.#object3ds.filter((obj) => {
      if (obj.properties?.namespace === "origin-marker") {
        this.threeLayer.removeMesh(obj)
        return false
      }
      return true
    })

    //show userLocation model
    if (this.#isOriginMarkerSpriteModeActive()) this.showUserLocation3dModel()
  }
  /**
   * END of Navigation
   ****************************/

  /**
   * hide/show venue 3dmodel
   **/

  hideVenueObjects = (): void => {
    const venueMarkers = this.#venueMarkers

    venueMarkers?.forEach((marker) => {
      const isAdd = marker?.properties?.isAdd

      if (isAdd) {
        this.gltfLayer.removeGeometry(marker)

        marker.properties.isAdd = false
      }
    })
  }

  showVenueObjects = (): void => {
    const venueMarkers = this.#venueMarkers

    venueMarkers?.forEach((marker) => {
      const isAdd = marker?.properties?.isAdd

      if (!isAdd) {
        this.gltfLayer.addGeometry(marker)

        marker.properties.isAdd = true
      }
    })
  }

  /**
   * Other functions
   */

  enableClick = () => this.map.config({ geometryEvents: true })
  disableClick = () => this.map.config({ geometryEvents: false })

  freeze = () => this.map.config({ zoomable: false, draggable: false })
  unfreeze = () => this.map.config({ zoomable: true, draggable: true })

  /**
   * render (frame)
   */
  getTargetViewCenter = (
    targetView,
    options = { offset: { top: 0, left: 0, right: 0, bottom: 0 } }
  ) => {
    // Extend the input targetView with default values from the current view if not provided
    const map = this.map
    const { offset } = options
    const { top = 0, left = 0, right = 0, bottom = 0 } = offset
    // Temporarily set the map to the target view to calculate the extent.
    // This approach requires that the map view can be adjusted without triggering a visible change.
    const originalState = {
      bearing: map.getBearing(),
      center: map.getCenter(),
      pitch: map.getPitch(),
      zoom: map.getZoom(),
    }

    const finalView = {
      bearing: _.isNil(targetView.bearing)
        ? map.getBearing()
        : targetView.bearing,
      center: _.isNil(targetView.center) ? map.getCenter() : targetView.center,
      pitch: _.isNil(targetView.pitch) ? map.getPitch() : targetView.pitch,
      zoom: _.isNil(targetView.zoom) ? map.getZoom() : targetView.zoom,
    }

    map.setView(finalView)

    // Calculate the visible extent at this state
    const projectedTargetCenter = map
      .coordinateToContainerPoint(finalView.center)
      .add(right / 2 - left / 2, bottom / 2 - top / 2)
    const adjustedTargetCenter = map.containerPointToCoordinate(
      projectedTargetCenter
    )
    // Reset the map to its original state to avoid any visible changes
    map.setView(originalState)
    return adjustedTargetCenter
  }

  setMaxExtent(extent: Extent) {
    return this.map.setMaxExtent(extent)
  }

  _rafId = null //to store requestAnimationFrame
  switchAnimation(mesh: any, nextName: "idle" | "move") {
    const actions = mesh.properties.actions
    if (!actions) return
    const current = mesh.properties.currentAction
    const next = actions[nextName]

    if (!next || current === next) return
    next.reset().play()
    current.crossFadeTo(next, 0.3, true)

    mesh.properties.currentAction = next
  }

  applyDirectionalSpriteMaterial(
    marker: SpriteMarker,
    state: "idle" | "walk",
    direction: string
  ) {
    if (
      marker.properties.currentState === state &&
      marker.properties.currentDirection === direction
    )
      return

    const material =
      marker.properties.directionalMaterials?.[state]?.[direction]
    if (!material) return

    marker.setSymbol(material)
    marker.properties.currentState = state
    marker.properties.currentDirection = direction
    applyOriginMarkerSpriteFrame(
      material.map,
      marker.properties.frameIndex ?? 0
    )
  }

  setOriginMarkerSpriteState(marker: SpriteMarker, state: "idle" | "walk") {
    this.applyDirectionalSpriteMaterial(
      marker,
      state,
      marker.properties.currentDirection ?? "S"
    )
  }

  // Projects a fixed world bearing onto the screen by subtracting the
  // current map bearing, so the marker turns as the map rotates around it —
  // like a physical object with a fixed heading. `worldBearing` is frozen
  // into `marker.properties` here and reused by #syncSpriteFacing on every
  // render frame; it is only ever recomputed by a caller explicitly choosing
  // a new heading (a walking step, or a fresh "face the camera" snapshot),
  // never by rotation itself.
  setOriginMarkerSpriteDirectionFromBearing(
    marker: SpriteMarker,
    worldBearing: number
  ) {
    marker.properties.worldBearing = worldBearing

    const direction = getOctantFromBearing(worldBearing - this.getBearing())
    this.applyDirectionalSpriteMaterial(
      marker,
      marker.properties.currentState ?? "idle",
      direction
    )
  }

  setOriginMarkerSpriteDirection(
    marker: SpriteMarker,
    from: Coordinate,
    to: Coordinate
  ) {
    if (!Array.isArray(from) || !Array.isArray(to)) return
    const worldBearing = getBearingBetweenPoints(
      { x: from[0], y: from[1] },
      { x: to[0], y: to[1] }
    )
    if (worldBearing == null) return

    this.setOriginMarkerSpriteDirectionFromBearing(marker, worldBearing)
  }

  // Snaps a sprite to face the given camera bearing right now, then leaves
  // it world-locked from that moment on (see setOriginMarkerSpriteDirectionFromBearing)
  // — it will drift out of facing the camera again as the map keeps rotating,
  // same as a physical object placed at that heading.
  faceOriginMarkerSpriteToCamera(targetCameraBearing?: number) {
    const originMarker = (this.#object3ds ?? []).find(
      (marker) => marker?.properties?.namespace === "origin-marker"
    )
    if (!(originMarker instanceof SpriteMarker)) return

    const cameraBearing = targetCameraBearing ?? this.getBearing()
    this.setOriginMarkerSpriteDirectionFromBearing(
      originMarker,
      cameraBearing + 180
    )
  }

  // Facing is derived, not stored. A sprite whose `worldBearing` has been
  // claimed (by a walking step, or by the arrival snapshot) is world-locked
  // and turns as the map rotates around it, like a physical object with a
  // fixed heading. One with no claim yet tracks the camera, so it reads as
  // facing the viewer.
  #syncSpriteFacing(marker: SpriteMarker, mapBearing: number) {
    const worldBearing = marker.properties.worldBearing ?? mapBearing + 180
    this.applyDirectionalSpriteMaterial(
      marker,
      marker.properties.currentState ?? "idle",
      getOctantFromBearing(worldBearing - mapBearing)
    )
  }

  // The user taking hold of the map is what turns an unclaimed sprite from
  // camera-tracking into world-locked: it freezes the heading it is
  // currently showing, so from here on it drifts out of facing the viewer
  // like everything else on the map. Sprites already claimed by a walking
  // step or the arrival snapshot keep their heading.
  handleClaimSpriteFacing = () => {
    const worldBearing = this.getBearing() + 180
    ;(this.#object3ds ?? []).forEach((object) => {
      if (!(object instanceof SpriteMarker)) return
      if (!object.properties?.directionalMaterials) return
      if (object.properties.worldBearing != null) return
      object.properties.worldBearing = worldBearing
    })
  }

  setOriginMarkerSpriteFrame(marker: SpriteMarker, now: number) {
    const frameDurationMs = 1000 / ORIGIN_MARKER_SPRITE_ANIMATION_FPS
    const frameIndex =
      Math.floor(now / frameDurationMs) % ORIGIN_MARKER_SPRITE_SHEET_FRAME_COUNT

    if (marker.properties.frameIndex === frameIndex) return
    marker.properties.frameIndex = frameIndex

    const material = marker.getSymbol()
    applyOriginMarkerSpriteFrame(material?.map, frameIndex)
  }

  animateSpriteOriginMarker(
    marker: SpriteMarker,
    from: Coordinate,
    to: Coordinate,
    duration = 2000
  ) {
    if (!Array.isArray(from) || !Array.isArray(to)) return
    if (this._rafId) {
      cancelAnimationFrame(this._rafId)
      this._rafId = null
    }

    this.setOriginMarkerSpriteState(marker, "walk")
    this.setOriginMarkerSpriteDirection(marker, from, to)

    const obj = (marker as any).object3d
    const start = performance.now()
    const fromVec = this.threeLayer.coordinateToVector3(
      new Coordinate(from[0], from[1], from[2])
    )
    const toVec = this.threeLayer.coordinateToVector3(
      new Coordinate(to[0], to[1], to[2])
    )

    const frame = (now: number) => {
      const t = Math.min((now - start) / duration, 1)
      obj.position.copy(fromVec.clone().lerp(toVec, t))

      if (t < 1) {
        this._rafId = requestAnimationFrame(frame)
      } else {
        this._rafId = null
        this.setOriginMarkerSpriteState(marker, "idle")
      }
    }
    this._rafId = requestAnimationFrame(frame)
  }

  animateOriginMarker(from: Coordinate, to: Coordinate, duration: number) {
    const originMarker = (this.#object3ds ?? []).find(
      (marker) => marker?.properties?.namespace === "origin-marker"
    )
    if (!originMarker) return

    if (originMarker instanceof SpriteMarker) {
      this.animateSpriteOriginMarker(originMarker, from, to, duration)
      return
    }
  }

  // The sprite walks between floors while properties.ordinal stays as
  // created, so keep it honest and let the one floor filter govern it like
  // every other object3d instead of exempting it in #showThreeObjectByOrdinal.
  setOriginMarkerOrdinal(ordinal: number): void {
    const originMarker = (this.#object3ds ?? []).find(
      (marker) => marker?.properties?.namespace === "origin-marker"
    )
    if (originMarker?.properties) originMarker.properties.ordinal = ordinal
  }

  changeOriginMarkerCoordinate(coord: Coordinate) {
    const originMarker = (this.#object3ds ?? []).find(
      (marker) => marker?.properties?.namespace === "origin-marker"
    )
    if (!originMarker) return
    if (this._rafId) {
      cancelAnimationFrame(this._rafId)
      this._rafId = null
    }
    const vec = this.threeLayer.coordinateToVector3(
      new Coordinate(coord[0], coord[1], coord[2] || 0)
    )
    originMarker.object3d.position.copy(vec)
    this.switchAnimation(originMarker, "idle")
    if (originMarker instanceof SpriteMarker) {
      this.setOriginMarkerSpriteState(originMarker, "idle")
    }
  }

  render() {
    // TODO: Update User Location marker position
    const view = this.map.getView()
    const currBearing = view.bearing
    this.threeLayer._needsUpdate = !this.threeLayer._needsUpdate
    if (this.threeLayer._needsUpdate) {
      // @ts-ignore
      this.threeLayer.redraw()
    }

    if (this.threeLayer) {
      // zoom = 19, opacity = 0
      // zoom = 18.5, opacity = 1
      const objectOpacity = _.clamp(38 - 2 * this.#zoomLevel, 0, 1)
      this.#objects.forEach((object) => {
        try {
          object.getObject3d().traverse((child) => {
            if (child.isMesh) child.material.opacity = objectOpacity
            //TODO: recheck this line if it actually works
            /** set Mesh visible = false when opacity is 0
             * and true when opacity > 0
             */
            child.visible = !!objectOpacity
          })
          object.getObject3d().visible = !!objectOpacity
        } catch (err) {
          console.log(err, object)
        }
      })

      // Re-apply zoom-styled bodies' material every frame (not just on
      // zoomend) so the fade animates smoothly while the user is actively zooming.
      this.#applyStyleOnZoom()

      if (this.#billboardObjects) {
        this.#billboardObjects.forEach((object) => {
          const objectScale = _.clamp(20 - 1 * this.#zoomLevel, 1, 1.05)
          object.getObject3d().scale.set(objectScale, objectScale, 1)
        })
      }

      /*
       * The layer appears while zooming in and disappears when zooming out,
       * fully vanishing when the Venue Model is visible (opacity: 1).
       */
      if (this.#isLayersFadingOnZoom) {
        const layerOpacity = _.clamp(1 - objectOpacity, 0, 1)
        // Fading 2D layers.
        LAYERS.forEach((layerKey) => {
          const layer = this.map.getLayer(layerKey)
          if (layer) layer.setOpacity(layerOpacity)
        })

        // Fading 3D units, such as extruded units.
        this.setFeatureObject3DsOpacity(layerOpacity)

        // Fading sprite markers, such as billboards and amenity markers.
        this.setSpriteMarkersOpacity(layerOpacity)
      }

      // Update bearing to each GroundLabel to determine if text should flip or not
      this.#groundObjects.forEach((gLabel) => {
        gLabel.bearing = currBearing
      })
      const dt = this.clock.getDelta()
      const now = performance.now()
      this.#object3ds.forEach((object) => {
        const mixer = object.properties?.mixer
        if (mixer) {
          mixer.update(dt)
        }
        if (
          object instanceof SpriteMarker &&
          object.properties?.directionalMaterials
        ) {
          this.#syncSpriteFacing(object, currBearing)
          this.setOriginMarkerSpriteFrame(object, now)
        }
      })
    }

    this.#animationsToRun.forEach(({ callback }) => callback(this))

    //update TWEEN.js animaton
    TWEEN.update()

    requestAnimationFrame(this.render.bind(this))
  }
}
